import React, { useEffect, useMemo, useRef, useState } from 'react';
import { spendInventoryUnit, useGameStore } from '../../stores/gameStore';
import { useCombatStore } from '../../stores/combatStore';
import { useInventoryStore } from '../../stores/inventoryStore';
import { combatService } from '../../services/combatService';
import { abilityService } from '../../services/abilityService';
import { getCastAbilityId, isUsableInCombat } from '../../utils/consumableSummary';
import { summarizeActiveEffects } from '../../utils/activeEffectSummary';
import { summarizeAbilityEffect } from '../../utils/abilitySummary';
import { UseItemPopover } from './UseItemPopover';
import { EntityStatus, isPlayerControlled, TargetType, AbilityDTO, IAttributes, IItemInstanceDTO } from '@hexcrawl/shared-utils';

export const ActionsPanel: React.FC = () => {
  const gameState = useGameStore((s) => s.gameState);
  const activeCombat = gameState?.activeCombat;

  const {
    inputMode, selectedAbilityId, selectedItemInstanceId,
    selectAbility, selectItemAbility, cancelAbilitySelection,
    queueBusy, enqueueResponse,
  } = useCombatStore();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [abilities, setAbilities] = useState<AbilityDTO[]>([]);
  const [itemsOpen, setItemsOpen] = useState(false);
  const itemMenuRef = useRef<HTMLDivElement>(null);

  const inventory = useInventoryStore((s) => s.inventory);
  const consumables = useMemo(() => inventory.filter(i => i.def.consumable), [inventory]);

  // Fetch abilities when character changes
  const characterAbilityIds = gameState?.character?.abilities;
  useEffect(() => {
    if (!characterAbilityIds || characterAbilityIds.length === 0) {
      setAbilities([]);
      return;
    }

    let cancelled = false;
    Promise.all(characterAbilityIds.map(id => abilityService.getAbility(id)))
      .then(fetchedAbilities => {
        if (!cancelled) setAbilities(fetchedAbilities);
      })
      .catch(err => {
        if (!cancelled) console.error('Failed to fetch abilities:', err);
      });

    return () => { cancelled = true; };
  }, [characterAbilityIds]);

  // Determine if it's the player's turn
  const { isPlayerTurn, currentEntityId } = useMemo(() => {
    if (!activeCombat || !gameState?.character || activeCombat.status !== 'active') {
      return { isPlayerTurn: false, currentEntityId: undefined };
    }

    const groups = activeCombat.groupOrder && activeCombat.groupOrder.length > 0
      ? activeCombat.groupOrder
      : Array.from(new Set(activeCombat.entityStates.map(c => c.groupId)));

    const fullOrder: string[] = [];
    for (const groupId of groups) {
      for (const entityState of activeCombat.entityStates) {
        if (entityState.groupId === groupId) fullOrder.push(entityState.entityId);
      }
    }

    const currentId = fullOrder.length > 0 ? fullOrder[activeCombat.turnIndex % fullOrder.length] : undefined;
    const currentState = currentId ? activeCombat.entityStates.find(e => e.entityId === currentId) : undefined;
    if (currentState?.status === EntityStatus.DEAD) {
      return { isPlayerTurn: false, currentEntityId: currentId };
    }

    return { isPlayerTurn: currentState != null && isPlayerControlled(currentState), currentEntityId: currentId };
  }, [activeCombat, gameState?.character]);

  // Buffs on the player, not on whoever is acting — this column describes the player's
  // own state, and it stays on screen through enemy turns.
  const playerBuffs = useMemo(() => {
    if (!activeCombat || !gameState?.character) return [];
    const playerState = activeCombat.entityStates.find(e => e.entityId === gameState.character!.id);
    return summarizeActiveEffects(playerState?.activeEffects, activeCombat.round);
  }, [activeCombat, gameState?.character]);

  const hasMoved = activeCombat?.turnHasMoved ?? false;
  const hasUsedAbility = activeCombat?.turnHasUsedAbility ?? false;
  const currentMP = gameState?.character?.currentMP ?? 0;
  const characterSkills = gameState?.character?.skills as Record<string, number | undefined> | undefined;
  const characterAttributes = gameState?.character?.attributes as IAttributes | undefined;

  const inputBlocked = queueBusy || isSubmitting;
  // A `usesTurn` item is unusable once the turn's ability action is gone, so it stops
  // counting toward the button's "N available" the moment that happens.
  const usableItemCount = consumables.filter(
    i => isUsableInCombat(i.def) && !(i.def.consumable?.usesTurn && hasUsedAbility),
  ).length;
  const itemButtonDisabled = !isPlayerTurn || inputBlocked || usableItemCount === 0;

  // The popover belongs to the player's own turn; anything that takes the turn away
  // (ending it, entering target selection) closes it.
  useEffect(() => {
    if (!isPlayerTurn || inputMode !== 'movement') setItemsOpen(false);
  }, [isPlayerTurn, inputMode]);

  const handleAbilityClick = (ability: AbilityDTO) => {
    if (hasUsedAbility || inputBlocked) return;
    setError(null);
    if (ability.target.type === TargetType.SELF && currentEntityId) {
      handleUseAbility(ability.id, currentEntityId);
    } else {
      selectAbility(ability.id);
    }
  };

  const handleUseAbility = async (abilityId: string, targetEntityId: string) => {
    if (!activeCombat) return;
    setIsSubmitting(true);
    setError(null);
    try {
      const response = await combatService.useAbility(activeCombat.id, abilityId, targetEntityId);
      if (enqueueResponse) await enqueueResponse(response);
      cancelAbilitySelection();
    } catch (err: any) {
      setError(err.message || 'Failed to use ability');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUseItem = async (item: IItemInstanceDTO) => {
    if (!activeCombat) return;
    setError(null);

    // An item that casts a spell needs a target first. It is committed from the scene
    // once the player confirms one, so nothing is spent if they cancel.
    const castAbilityId = getCastAbilityId(item.def);
    if (castAbilityId) {
      try {
        // The scene reads this ability from the synchronous cache to decide AoE routing
        // and draw the range, so make sure it is there before target mode opens.
        await abilityService.getAbility(castAbilityId);
      } catch (err: any) {
        setError(err.message || 'Failed to load the scroll\'s spell');
        return;
      }
      selectItemAbility(item.id, castAbilityId);
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await combatService.useItem(activeCombat.id, item.id);
      // The response is a combat action and carries no inventory, so the spent unit
      // is taken off the stack locally to match the server.
      spendInventoryUnit(item.id);
      if (enqueueResponse) await enqueueResponse(response);
    } catch (err: any) {
      setError(err.message || 'Failed to use item');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEndTurn = async () => {
    if (!activeCombat) return;
    setIsSubmitting(true);
    setError(null);
    useCombatStore.getState().setQueueBusy(true);
    try {
      const response = await combatService.endTurn(activeCombat.id);
      if (enqueueResponse) await enqueueResponse(response);
      cancelAbilitySelection();
    } catch (err: any) {
      setError(err.message || 'Failed to end turn');
      useCombatStore.getState().setQueueBusy(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!activeCombat) return null;

  const selectedAbility = abilities.find(a => a.id === selectedAbilityId);
  // A scroll's spell is not on the ability bar, so the banner names the item instead —
  // that is what the player picked and what is at stake if they confirm.
  const selectedItem = consumables.find(i => i.id === selectedItemInstanceId);
  const targetSelectionName = selectedItem?.def.name ?? selectedAbility?.name;

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      padding: '6px 16px 8px',
      color: '#a8d1ff',
      minHeight: '72px',
      gap: '4px',
    }}>
      {/* Main row: [left] [center] [right] */}
      <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>

        {/* Left: move hint + turn state indicators */}
        <div style={{ minWidth: '100px', display: 'flex', flexDirection: 'column', gap: '3px', fontSize: '12px' }}>
          {isPlayerTurn && inputMode === 'movement' && !hasMoved && !inputBlocked && (
            <span style={{ color: '#88ccff' }}>Click a tile to move</span>
          )}
          {isPlayerTurn && (
            <>
              <span style={{ color: hasMoved ? '#88ff88' : '#ffff88' }}>
                {hasMoved ? '\u2713 Moved' : '\u25CB Can Move'}
              </span>
              <span style={{ color: hasUsedAbility ? '#88ff88' : '#ffff88' }}>
                {hasUsedAbility ? '\u2713 Ability Used' : '\u25CB Can Use Ability'}
              </span>
            </>
          )}
          {!isPlayerTurn && (
            <span style={{ color: '#ff8888', fontSize: '13px' }}>Not your turn</span>
          )}
          {playerBuffs.map((buff, i) => (
            <span key={`${buff.label}-${i}`} style={{ color: '#ffd27f' }}>
              {buff.label} ({buff.roundsRemaining})
            </span>
          ))}
        </div>

        {/* Center: ability buttons or target-selection banner */}
        <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          {inputMode === 'target-selection' && targetSelectionName && (
            <div style={{
              background: 'rgba(255, 200, 100, 0.15)',
              border: '1px solid #ffcc66',
              borderRadius: '6px',
              padding: '10px 18px',
              display: 'flex',
              alignItems: 'center',
              gap: '14px',
              fontSize: '14px',
            }}>
              <span>Select target for <strong>{targetSelectionName}</strong></span>
              <button
                onClick={() => cancelAbilitySelection()}
                style={{
                  padding: '6px 14px',
                  background: '#663333',
                  border: '1px solid #ff6666',
                  borderRadius: '4px',
                  color: '#ffcccc',
                  cursor: 'pointer',
                  fontSize: '13px',
                }}
              >
                Cancel
              </button>
            </div>
          )}
          {inputMode === 'movement' && abilities.map((ability) => {
            const notEnoughMp = ability.mpCost > currentMP;
            const abilityDisabled = !isPlayerTurn || inputBlocked || hasUsedAbility || notEnoughMp;

            const { minDamage: minDmg, maxDamage: maxDmg, casterSkill, skillRank } =
              summarizeAbilityEffect(ability, characterSkills, characterAttributes);

            const borderColor = hasUsedAbility ? '#333' : notEnoughMp ? '#553333' : '#3a86ff';
            const textColor = hasUsedAbility ? '#666' : notEnoughMp ? '#aa6666' : '#d9ecff';
            const bgColor = hasUsedAbility ? '#1a1a2a' : notEnoughMp ? '#2a1a1a' : '#16354a';

            return (
              <button
                key={ability.id}
                onClick={() => handleAbilityClick(ability)}
                disabled={abilityDisabled}
                title={notEnoughMp ? `Not enough MP (need ${ability.mpCost}, have ${currentMP})` : undefined}
                style={{
                  padding: '6px 12px',
                  background: bgColor,
                  border: `2px solid ${borderColor}`,
                  borderRadius: '6px',
                  color: textColor,
                  cursor: abilityDisabled ? 'not-allowed' : 'pointer',
                  opacity: abilityDisabled ? 0.5 : 1,
                  textAlign: 'left',
                  minWidth: '110px',
                }}
              >
                <div style={{ fontWeight: 'bold', fontSize: '13px' }}>{ability.name}</div>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '2px' }}>
                  {minDmg != null && maxDmg != null && (
                    <span style={{ fontSize: '11px', color: '#ff9966' }}>{minDmg}–{maxDmg} dmg</span>
                  )}
                  {skillRank != null && casterSkill != null && (
                    <span style={{ fontSize: '11px', color: '#88c0ff' }}>+{skillRank} {casterSkill}</span>
                  )}
                  {ability.mpCost > 0 && (
                    <span style={{ fontSize: '11px', color: notEnoughMp ? '#cc5555' : '#88aaff' }}>{ability.mpCost} MP</span>
                  )}
                </div>
              </button>
            );
          })}

          {/* Use Item — one button for the whole bag, so the bar does not grow with it */}
          {inputMode === 'movement' && (
            <div ref={itemMenuRef} style={{ position: 'relative' }}>
              <button
                onClick={() => setItemsOpen(open => !open)}
                disabled={itemButtonDisabled}
                title={usableItemCount === 0 ? 'Nothing in your bag can be used here' : undefined}
                style={{
                  padding: '6px 12px',
                  background: itemButtonDisabled ? '#1a1a2a' : '#1a3a2a',
                  border: `2px solid ${itemButtonDisabled ? '#333' : '#66cc88'}`,
                  borderRadius: '6px',
                  color: itemButtonDisabled ? '#666' : '#ccffdd',
                  cursor: itemButtonDisabled ? 'not-allowed' : 'pointer',
                  opacity: itemButtonDisabled ? 0.5 : 1,
                  textAlign: 'left',
                  minWidth: '110px',
                }}
              >
                <div style={{ fontWeight: 'bold', fontSize: '13px' }}>Use Item</div>
                <div style={{ fontSize: '11px', color: itemButtonDisabled ? '#666' : '#88ccaa', marginTop: '2px' }}>
                  {usableItemCount > 0 ? `${usableItemCount} available` : 'nothing to use'}
                </div>
              </button>

              {itemsOpen && (
                <UseItemPopover
                  inventory={consumables}
                  disabled={inputBlocked}
                  abilityActionSpent={hasUsedAbility}
                  boundaryRef={itemMenuRef}
                  onSelect={handleUseItem}
                  onClose={() => setItemsOpen(false)}
                />
              )}
            </div>
          )}
        </div>

        {/* Right: end turn */}
        <div style={{ minWidth: '100px', display: 'flex', justifyContent: 'flex-end' }}>
          {isPlayerTurn && inputMode === 'movement' && (
            <button
              onClick={handleEndTurn}
              disabled={inputBlocked}
              style={{
                padding: '8px 20px',
                background: '#2a4a3a',
                border: '2px solid #66cc88',
                borderRadius: '6px',
                color: '#ccffdd',
                cursor: inputBlocked ? 'not-allowed' : 'pointer',
                fontWeight: 'bold',
                fontSize: '14px',
              }}
            >
              {inputBlocked ? 'Processing...' : 'End Turn'}
            </button>
          )}
        </div>
      </div>

      {/* Error */}
      {error && (
        <div style={{
          textAlign: 'center',
          background: 'rgba(255, 100, 100, 0.2)',
          border: '1px solid #ff6666',
          borderRadius: '4px',
          padding: '4px 8px',
          color: '#ffaaaa',
          fontSize: '12px',
        }}>
          {error}
        </div>
      )}
    </div>
  );
};
